import type { useFetcher } from '@remix-run/react'
import { EarthIcon, HistoryIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getCountryName, getSortedCountryList } from '~/helpers/country'
import { makeShopOwnPath } from '~/helpers/routes'
import { useShop } from '~/hooks/data/use-shop'
import type { ShippingCountryCode } from '~/schemas/country'
import type { ShopShippingOptions } from '~/schemas/shop'
import { Alert, Listbox } from '~/ui'
import { cva } from '~/utils/classnames'
import { addDaysToTodayFormatted } from '~/utils/date'

import { isValidShippingCountry } from './cart-utils'

const propertyStyle = cva({
  base: 'text-sm font-medium [&>.lucide]:size-4.5! flex gap-2 items-center [&>.lucide]:shrink-0 flex-wrap',
})

interface CartShippingProps {
  buyerCountry?: ShippingCountryCode
  fetcher: ReturnType<typeof useFetcher>
  className?: string
}

export const CartShipping = ({
  buyerCountry,
  fetcher,
  className,
}: CartShippingProps) => {
  const [search, setSearch] = useState('')
  const { t } = useTranslation()
  const shop = useShop()

  const availableCountries = useMemo(() => {
    return (
      (shop.shippingOptions as ShopShippingOptions)?.flatMap(
        ({ countryCodes }) => countryCodes
      ) || []
    )
  }, [shop.shippingOptions])

  const [selectedCountry, setSelectedCountry] = useState(
    isValidShippingCountry(buyerCountry, shop) ? buyerCountry : undefined
  )

  useEffect(() => {
    if (buyerCountry && isValidShippingCountry(buyerCountry, shop)) {
      setSelectedCountry(buyerCountry)
    }
  }, [buyerCountry, shop])

  const selectedShippingOption = useMemo(() => {
    const code = selectedCountry as ShippingCountryCode
    const options = shop.shippingOptions as ShopShippingOptions
    if (!code || !options) return null

    return options.find((option) => option.countryCodes.includes(code)) || null
  }, [selectedCountry, shop.shippingOptions])

  const handleCountryChange = (country?: ShippingCountryCode) => {
    setSelectedCountry(country)

    if (country) {
      fetcher.submit(
        { buyerCountry: country },
        { method: 'post', action: makeShopOwnPath(shop, '/session?index') }
      )
    }
  }

  const options = useMemo(() => {
    return getSortedCountryList(availableCountries).filter((item) =>
      item.label.toLowerCase().includes(search.toLowerCase())
    )
  }, [availableCountries, search])

  return (
    <div className={className}>
      {/* no shipping option for buyer country */}
      {buyerCountry && !selectedCountry && (
        <Alert
          type="error"
          className="mb-3"
          title={t(
            'page.products.view_product.unfortunately_dont_ship_to_country',
            {
              country: getCountryName(buyerCountry),
            }
          )}
        />
      )}
      <div className={propertyStyle()}>
        <EarthIcon />
        <Listbox
          value={selectedCountry}
          onChange={handleCountryChange}
          matchReferenceWidth={false}
        >
          <Listbox.Trigger
            size="sm"
            className="w-fit"
            placeholder={t('page.settings.shipping.select_country')}
          >
            {selectedCountry ? getCountryName(selectedCountry) : undefined}
          </Listbox.Trigger>
          <Listbox.Options className="max-w-64">
            <Listbox.SearchInput
              placeholder="Search country"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
            />
            {options.map((item) => (
              <Listbox.Option key={item.value} value={item.value}>
                {item.label}
              </Listbox.Option>
            ))}
            {options.length === 0 && (
              <Listbox.Empty>
                {t('page.settings.shipping.ships_from_search_no_results')}
              </Listbox.Empty>
            )}
          </Listbox.Options>
        </Listbox>
        {selectedShippingOption && (
          <p className="text-sm">
            {t('page.products.view_product.delivery_around', {
              date: addDaysToTodayFormatted(
                selectedShippingOption.days + (shop.dispachWithinDays || 0)
              ),
            })}
          </p>
        )}
      </div>
      {shop.returnWithinDays && (
        <p className={propertyStyle({ className: 'mt-1.5' })}>
          <HistoryIcon />
          <span>
            {t('page.products.view_product.returns_within', {
              days: shop.returnWithinDays,
            })}
          </span>
        </p>
      )}
    </div>
  )
}
